import * as React from "react";
import { useState } from "react";
import { Field, withTypes } from "react-final-form";
import axios from "axios";
import {
  Button,
  CardActions,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch } from "../getConfig";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  },
  form: {
    padding: "0 1em 1em 1em",
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
  },
  input: {
    marginTop: "1em",
    width: "100%",
  },
  actions: {
    padding: "0 1em 1em 1em",
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
  },
  tableCell: {
    padding: "4px 10px",
    border: "1px solid #ddd",
    fontSize: "0.85rem",
  },
  tableCellHead: {
    padding: "6px 10px",
    border: "1px solid #ddd",
    fontWeight: 700,
    fontSize: "0.85rem",
    backgroundColor: "#f5f5f5",
  },
  section: {
    width: "100%",
    maxWidth: 1200,
    margin: "1em auto",
    padding: "0 1em",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "1.1rem",
    margin: "1em 0 0.5em",
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  paginationRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "8px 0",
    gap: "8px",
  },
  bpsSection: {
    marginTop: "1.5em",
    padding: "1em",
    border: "1px solid #ddd",
    borderRadius: 8,
    backgroundColor: "#fafafa",
  },
}));

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    fullWidth
  />
);

interface FormValues {
  EmailOrId?: string;
}

const { Form } = withTypes<FormValues>();

const getStatusColor = (status: string): "default" | "primary" | "secondary" => {
  switch (status) {
    case "active":
    case "processed":
      return "primary";
    case "cancelled":
    case "failed":
      return "secondary";
    default:
      return "default";
  }
};

const getReasonColor = (status: string): string => {
  switch (status) {
    case "failed":
      return "#d32f2f";
    case "cancelled":
      return "#ed6c02";
    default:
      return "inherit";
  }
};

const AutoSaveDashboard = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [configs, setConfigs] = useState<any>(null);
  const [configPage, setConfigPage] = useState(1);

  // Debits state
  const [debitsLoading, setDebitsLoading] = useState(false);
  const [debits, setDebits] = useState<any>(null);
  const [debitPage, setDebitPage] = useState(1);

  // BPS Payer state
  const [bpsData, setBpsData] = useState<any>(null);
  const [updatePayerStatus, setUpdatePayerStatus] = useState("");
  const [updateEnableDD, setUpdateEnableDD] = useState(false);
  const [bpsUpdateLoading, setBpsUpdateLoading] = useState(false);

  const handleSubmit = async (inputData: FormValues) => {
    if (!inputData.EmailOrId) {
      notify("Please enter a user ID or email", "error");
      return;
    }

    setLoading(true);
    setConfigs(null);
    setBpsData(null);
    setDebits(null);
    setConfigPage(1);
    setDebitPage(1);

    try {
      const isEmail = isNaN(Number(inputData.EmailOrId));
      const params = isEmail
        ? { email: inputData.EmailOrId }
        : { user_id: inputData.EmailOrId };

      // Fetch auto-save configs
      const configResponse: any = await axios.get(ENDPOINTS.ADMIN_AUTO_SAVES, getConfigGet({ ...params, page: 1, pageSize: 20 }));
      setConfigs(configResponse.data);

      // Resolve user_id for subsequent calls
      let resolvedUserId: number | null = null;
      if (isEmail && configResponse.data?.data?.length > 0) {
        resolvedUserId = configResponse.data.data[0].user_id;
      } else if (!isEmail) {
        resolvedUserId = Number(inputData.EmailOrId);
      }
      setUserId(resolvedUserId);

      // Fetch all debits for the user (not filtered by config)
      const debitsResponse: any = await axios.get(ENDPOINTS.ADMIN_AUTO_SAVE_DEBITS, getConfigGet({
        ...params,
        page: 1,
        pageSize: 20,
        sortBy: "createdAt",
        sortOrder: "DESC",
      }));
      setDebits(debitsResponse.data);

      // Fetch BPS payer
      const bpsResponse: any = await axios.get(ENDPOINTS.ADMIN_BPS_PAYER, getConfigGet(params));
      setBpsData(bpsResponse.data?.data);
      if (bpsResponse.data?.data) {
        setUpdateEnableDD(bpsResponse.data.data.enable_direct_debit);
        if (bpsResponse.data.data.payers?.length > 0) {
          setUpdatePayerStatus(bpsResponse.data.data.payers[0].payer_status || "");
        }
      }

      notify("Data loaded successfully", "info");
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to fetch data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleConfigPageChange = async (newPage: number) => {
    if (!userId) return;
    setConfigPage(newPage);
    setLoading(true);
    try {
      const response: any = await axios.get(ENDPOINTS.ADMIN_AUTO_SAVES, getConfigGet({ user_id: userId, page: newPage, pageSize: 20 }));
      setConfigs(response.data);
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to fetch configs", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDebitPageChange = async (newPage: number) => {
    if (!userId) return;
    setDebitPage(newPage);
    setDebitsLoading(true);
    try {
      const response: any = await axios.get(ENDPOINTS.ADMIN_AUTO_SAVE_DEBITS, getConfigGet({
        user_id: userId,
        page: newPage,
        pageSize: 20,
        sortBy: "createdAt",
        sortOrder: "DESC",
      }));
      setDebits(response.data);
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to fetch debits", "error");
    } finally {
      setDebitsLoading(false);
    }
  };

  const handleBpsUpdate = async () => {
    if (!userId || !bpsData?.payers?.length) return;
    setBpsUpdateLoading(true);
    try {
      const payerId = bpsData.payers[0].id;
      await axios.patch(
        `${ENDPOINTS.ADMIN_BPS_PAYER}/${payerId}`,
        {
          user_id: userId,
          payer_status: updatePayerStatus,
          enable_direct_debit: updateEnableDD,
        },
        { headers: getConfigPatch() }
      );
      notify("BPS payer updated successfully", "info");

      // Refresh BPS data
      const bpsResponse: any = await axios.get(ENDPOINTS.ADMIN_BPS_PAYER, getConfigGet({ user_id: userId }));
      setBpsData(bpsResponse.data?.data);
    } catch (error: any) {
      notify(error?.response?.data?.message || "Failed to update BPS payer", "error");
    } finally {
      setBpsUpdateLoading(false);
    }
  };

  return (
    <div className={classes.main}>
      <div style={{ width: "100%", maxWidth: 520, marginTop: "2em" }}>
        <Typography variant="h5" style={{ textAlign: "center", marginBottom: "1em" }}>
          Auto-Save Dashboard
        </Typography>
        <Form
          onSubmit={handleSubmit}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} noValidate>
              <div className={classes.form}>
                <div className={classes.input}>
                  <Field
                    autoFocus
                    name="EmailOrId"
                    component={renderInput}
                    label="User ID or Email"
                    disabled={loading}
                  />
                </div>
              </div>
              <CardActions className={classes.actions}>
                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  disabled={loading}
                  fullWidth
                >
                  {loading ? <CircularProgress size={25} thickness={2} /> : "Search"}
                </Button>
              </CardActions>
            </form>
          )}
        />
      </div>

      {/* Auto-Save Configs Table */}
      {configs && (
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Auto-Save Configurations ({configs.pagination?.total || 0} total)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCellHead}>ID</TableCell>
                <TableCell className={classes.tableCellHead}>Mode</TableCell>
                <TableCell className={classes.tableCellHead}>Amount</TableCell>
                <TableCell className={classes.tableCellHead}>Min</TableCell>
                <TableCell className={classes.tableCellHead}>Max</TableCell>
                <TableCell className={classes.tableCellHead}>Status</TableCell>
                <TableCell className={classes.tableCellHead}>From</TableCell>
                <TableCell className={classes.tableCellHead}>To</TableCell>
                <TableCell className={classes.tableCellHead}>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.data?.map((config: any) => (
                <TableRow key={config.id}>
                  <TableCell className={classes.tableCell}>{config.id}</TableCell>
                  <TableCell className={classes.tableCell}>{config.mode}</TableCell>
                  <TableCell className={classes.tableCell}>{config.amount || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>{config.min_amount || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>{config.max_amount || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>
                    <Chip
                      label={config.status}
                      color={getStatusColor(config.status)}
                      size="small"
                      className={classes.statusChip}
                    />
                  </TableCell>
                  <TableCell className={classes.tableCell}>{config.from_date}</TableCell>
                  <TableCell className={classes.tableCell}>{config.to_date || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>
                    {config.created_at ? new Date(config.created_at).toLocaleDateString() : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {configs.pagination && (
            <div className={classes.paginationRow}>
              <Typography variant="body2">
                Page {configs.pagination.page} of {configs.pagination.total_pages}
              </Typography>
              <IconButton
                size="small"
                disabled={!configs.pagination.has_previous_page}
                onClick={() => handleConfigPageChange(configPage - 1)}
              >
                <NavigateBeforeIcon />
              </IconButton>
              <IconButton
                size="small"
                disabled={!configs.pagination.has_next_page}
                onClick={() => handleConfigPageChange(configPage + 1)}
              >
                <NavigateNextIcon />
              </IconButton>
            </div>
          )}
        </div>
      )}

      {/* Debits Table */}
      {debits && (
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Debits ({debits.pagination?.total || 0} total)
          </Typography>
          {debitsLoading ? (
            <div style={{ textAlign: "center", padding: "2em" }}>
              <CircularProgress />
            </div>
          ) : debits.data?.length > 0 ? (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableCellHead}>ID</TableCell>
                    <TableCell className={classes.tableCellHead}>Amount</TableCell>
                    <TableCell className={classes.tableCellHead}>Collection Date</TableCell>
                    <TableCell className={classes.tableCellHead}>Instruction Date</TableCell>
                    <TableCell className={classes.tableCellHead}>Status</TableCell>
                    <TableCell className={classes.tableCellHead}>Is Min</TableCell>
                    <TableCell className={classes.tableCellHead}>Algo Category</TableCell>
                    <TableCell className={classes.tableCellHead}>Config ID</TableCell>
                    <TableCell className={classes.tableCellHead}>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {debits.data.map((debit: any) => (
                    <TableRow key={debit.id}>
                      <TableCell className={classes.tableCell}>{debit.id}</TableCell>
                      <TableCell className={classes.tableCell}>{debit.amount}</TableCell>
                      <TableCell className={classes.tableCell}>{debit.collection_date}</TableCell>
                      <TableCell className={classes.tableCell}>{debit.instruction_date}</TableCell>
                      <TableCell className={classes.tableCell}>
                        <Chip
                          label={debit.status}
                          color={getStatusColor(debit.status)}
                          size="small"
                          className={classes.statusChip}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>{debit.is_min ? "Yes" : "No"}</TableCell>
                      <TableCell className={classes.tableCell}>{debit.algo_category_id || "-"}</TableCell>
                      <TableCell className={classes.tableCell}>{debit.monthly_min_max_id || "-"}</TableCell>
                      <TableCell
                        className={classes.tableCell}
                        style={{ color: getReasonColor(debit.processed_status), maxWidth: 300 }}
                      >
                        {debit.reason || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className={classes.paginationRow}>
                <Typography variant="body2">
                  Page {debits.pagination?.page} of {debits.pagination?.total_pages} ({debits.pagination?.total} total)
                </Typography>
                <IconButton
                  size="small"
                  disabled={!debits.pagination?.has_previous_page}
                  onClick={() => handleDebitPageChange(debitPage - 1)}
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={!debits.pagination?.has_next_page}
                  onClick={() => handleDebitPageChange(debitPage + 1)}
                >
                  <NavigateNextIcon />
                </IconButton>
              </div>
            </>
          ) : (
            <Typography>No debits found for this user</Typography>
          )}
        </div>
      )}

      {/* BPS Payer Section */}
      {bpsData && (
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>BPS Payer</Typography>
          <div className={classes.bpsSection}>
            {bpsData.payers?.length > 0 ? (
              <>
                <Table size="small" style={{ marginBottom: "1em" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableCellHead}>ID</TableCell>
                      <TableCell className={classes.tableCellHead}>Payer Reference</TableCell>
                      <TableCell className={classes.tableCellHead}>Payer Status</TableCell>
                      <TableCell className={classes.tableCellHead}>Error Reason</TableCell>
                      <TableCell className={classes.tableCellHead}>Cancelled At</TableCell>
                      <TableCell className={classes.tableCellHead}>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bpsData.payers.map((payer: any) => (
                      <TableRow key={payer.id}>
                        <TableCell className={classes.tableCell}>{payer.id}</TableCell>
                        <TableCell className={classes.tableCell}>{payer.payer_reference}</TableCell>
                        <TableCell className={classes.tableCell}>
                          <Chip
                            label={payer.payer_status || "N/A"}
                            color={payer.payer_status === "Active" ? "primary" : "secondary"}
                            size="small"
                            className={classes.statusChip}
                          />
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {payer.error_reason
                            ? `${payer.error_reason.reason_code}: ${payer.error_reason.reason_explanation}`
                            : "-"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {payer.cancelled_at ? new Date(payer.cancelled_at).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {payer.createdAt ? new Date(payer.createdAt).toLocaleDateString() : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Update Controls */}
                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: "0.5em" }}>
                  Update BPS Payer
                </Typography>
                <div style={{ display: "flex", gap: "1em", alignItems: "center", flexWrap: "wrap" }}>
                  <FormControl style={{ minWidth: 200 }}>
                    <InputLabel>Payer Status</InputLabel>
                    <Select
                      value={updatePayerStatus}
                      onChange={(e) => setUpdatePayerStatus(e.target.value as string)}
                    >
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Creation Pending">Creation Pending</MenuItem>
                      <MenuItem value="Cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={updateEnableDD}
                        onChange={(e) => setUpdateEnableDD(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enable Direct Debit"
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleBpsUpdate}
                    disabled={bpsUpdateLoading}
                  >
                    {bpsUpdateLoading ? <CircularProgress size={20} /> : "Update"}
                  </Button>
                </div>
              </>
            ) : (
              <Typography>No BPS payer record found for this user</Typography>
            )}
            <Typography variant="body2" style={{ marginTop: "0.5em" }}>
              Enable Direct Debit: <strong>{bpsData.enable_direct_debit ? "Yes" : "No"}</strong>
            </Typography>
          </div>
        </div>
      )}

      <Notification />
    </div>
  );
};

export default AutoSaveDashboard;
